import { Component, DestroyRef, inject, Input, OnInit, signal, WritableSignal } from '@angular/core';
import { AuthenticatedUserService, UserService } from '@markant-platform/shared/user/data-access';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  catchError,
  combineLatest,
  EMPTY,
  firstValueFrom,
  map,
  mergeMap,
  Observable,
  of,
  skip,
  Subject,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { UsageRightService } from '@markant-platform/usage-right/data-access';
import { UsageRight } from '@markant-platform/usage-right/model';
import { AuthorizerService } from '@markant-platform/authorizer/data-access';
import { Group, GroupRoleAssignment, Role } from '@markant-platform/authorizer/model';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  isFormUnChanged,
  RegexPattern,
  removeWhitespace,
  setEmptyStringPropertiesToNull,
} from '@markant-platform/shared/util-helpers';
import { formatAddress } from '@markant-platform/shared/organization/util';
import { SnackbarService } from '@markant-platform/shared/snackbar/feature';
import {
  CartridgeVariantAlternativeOwnerResponseDto,
  CartridgeVariantCurrentOwnersResponseDto,
  compareRole,
  MembershipOriginInformation,
  OrganizationUserDTO,
  StaticRole,
  UserOrganizationStatus,
  UserOrganizationStatusMap,
  UserRole,
  UserUpdateDTO,
} from '@markant-platform/shared/user/model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MarkantElementsModule } from '@markant/elements-angular';
import { SharedUiButtonsModule } from '@markant-platform/shared/ui-buttons';
import { SharedOrganizationFeatureModule } from '../shared-organization-feature.module';
import { CommonModule, LocationStrategy } from '@angular/common';
import { AuditReasonDialogComponent, SharedDialogFeatureModule } from '@markant-platform/shared/dialog/feature';
import { ModalBridgeComponent } from '@markant-platform/shared/ui-modal';
import { UiCardComponent, UiCardItemComponent } from '@markant-platform/shared/ui-card';
import { DefaultValuePipe } from '@markant-platform/shared/util-pipes';
import { UiPageHeaderComponent } from '@markant-platform/shared/ui-components';
import {
  ServiceResponsibilityAssignNewAdminComponent,
  ServiceRolesAssignmentsDialogComponent,
} from '@markant-platform/shared/authorizer/feature';
import { CardMembershipOriginComponent } from './cards/card-membership-origin/card-membership-origin.component';
import { OrganizationMemberDetailsFormComponent } from './organization-member-details-form/organization-member-details-form.component';
import { LocaleService } from '@markant-platform/shared/util-locale';
import {
  CartridgeRoleAssignmentDataService,
  DisabledRoleLimitsStrategyService,
  OrganizationMemberRoleLimitsStrategyService,
} from '@markant-platform/shared/authorizer-utils';
import { HttpErrorResponse } from '@angular/common/http';
import { AppConfigService, AppFeature } from '@markant-platform/shared/data-access';
import { NoMailChangeReason } from './models/no-mail-change-reason';
import { Address, SalutationType } from 'data-model';

export type MemberDetailsDemoConfig = {
  user: OrganizationUserDTO;
  usageRights: UsageRight[];
  cartridgeRoleAssignments: Map<string, UserRole[]>;
  userGroups: Group[];
  groupRoleAssignments: Record<string, GroupRoleAssignment[]>;
};

@Component({
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    MarkantElementsModule,
    SharedOrganizationFeatureModule,
    SharedDialogFeatureModule,
    RouterLink,
    FormsModule,
    ReactiveFormsModule,
    SharedUiButtonsModule,
    ModalBridgeComponent,
    UiCardComponent,
    UiCardItemComponent,
    DefaultValuePipe,
    ServiceRolesAssignmentsDialogComponent,
    UiPageHeaderComponent,
    CardMembershipOriginComponent,
    OrganizationMemberDetailsFormComponent,
    ServiceResponsibilityAssignNewAdminComponent,
    AuditReasonDialogComponent,
  ],
  selector: 'markant-platform-organization-member-details',
  templateUrl: './organization-member-details.component.html',
  styleUrls: ['./organization-member-details.component.scss'],
})
export class OrganizationMemberDetailsComponent implements OnInit {
  @Input() demoContent?: MemberDetailsDemoConfig;

  userId!: string;
  organizationId!: string;
  user!: OrganizationUserDTO;
  formBuilder = new FormBuilder();
  emptyAddress: Address = {
    street: '',
    streetNumber: '',
    zipCode: '',
    city: '',
    countryCode: '',
  };
  userOrganizationForm = this.formBuilder.group({
    salutation: ['', Validators.required],
    firstname: ['', [Validators.required, Validators.pattern(RegexPattern.NAME_PATTERN)]],
    lastname: ['', [Validators.required, Validators.pattern(RegexPattern.NAME_PATTERN)]],
    email: [{ value: '', disabled: true }, [Validators.required, Validators.pattern(RegexPattern.EMAIL_PATTERN)]],
    phone: ['', Validators.pattern(RegexPattern.PHONE_PATTERN)],
    language: ['', Validators.required],
    organizationName: [{ value: '', disabled: true }],
    department: '',
    position: '',
    address: this.formBuilder.group(Object.assign({}, this.emptyAddress)),
  });
  organizationRoleForm = this.formBuilder.group({
    organizationRole: '',
  });
  form = new FormGroup({
    user: this.userOrganizationForm,
    role: this.organizationRoleForm,
  });
  isLoading = true;
  editingMode = false;
  showLoadRolesError = false;

  userRoles: string[] = [];
  userGroups: Group[] = [{ id: 'group', displayName: 'groupName' } as Group];
  groupRoleAssignments: Record<string, GroupRoleAssignment[]> = {};
  usageRights!: UsageRight[];
  reducedCartridgeRoleAssignments = new Map<string, UserRole[]>();
  calloutColorScheme: 'info' | 'error' | 'success' | 'warning' = 'info';
  calloutMsgKey = '';

  currentUserIsPlatformAdmin: boolean | undefined = false;
  currentUserIsOrgAdmin: boolean | undefined = false;
  currentUserIsAllowedToManageGroupAssignments: boolean | undefined = false;
  currentUserOrganizationId!: string;
  currentUserShowsSelf: boolean | undefined = false;
  isRoleAssignmentDialogOpen = false;
  isDeleteConfirmationDialogOpen = false;
  isResendInvitationMailDialogOpen = false;
  isResendVerificationEmailOpen = false;
  removeEmployeeDialogOpen = false;
  isAuditReasonDialogOpen = false;

  deleteConfirmationDialogCallback: (() => void) | undefined;

  deleteConformationDialogHeadlineTranslationKey:
    | 'user.details.role-assignment.deleteDialogHeadline'
    | 'user.details.role-assignment.deleteDialogHeadlinePlural' = 'user.details.role-assignment.deleteDialogHeadline';
  deleteConformationDialogTextTranslationKey:
    | 'user.details.role-assignment.deleteDialogText'
    | 'user.details.role-assignment.deleteDialogTextPlural' = 'user.details.role-assignment.deleteDialogText';
  assignableOrganizationRoles: Role[] = [];
  isAssignAdminForServiceBookingResponsibilityOpen?: boolean = false;
  owners: CartridgeVariantCurrentOwnersResponseDto[] = [];
  alternativeOwners: CartridgeVariantAlternativeOwnerResponseDto[] = [];
  submitDisabled = true;
  initialFormValues = {};
  membershipOrigin$?: Observable<MembershipOriginInformation>;

  confirmMailChangeDialogOpen = signal(false);
  confirmedMailChange$ = new Subject<boolean>();
  noEmailChangeReason: WritableSignal<NoMailChangeReason | undefined> = signal(undefined);

  static handledErrorCodes = ['USE-021', 'USE-024'];

  protected readonly StaticRole = StaticRole;
  protected readonly formatAddress = formatAddress;
  protected readonly UserOrganizationStatusMap = UserOrganizationStatusMap;
  protected readonly UserOrganizationStatus = UserOrganizationStatus;
  protected readonly FormGroup = FormGroup;

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly translocoService = inject(TranslocoService);
  private readonly userService = inject(UserService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly location = inject(LocationStrategy);
  private readonly usageRightService = inject(UsageRightService);
  private readonly authorizerService = inject(AuthorizerService);
  private readonly authenticatedUserService = inject(AuthenticatedUserService);
  private readonly cartridgeRoleAssignmentDataService = inject(CartridgeRoleAssignmentDataService);
  private readonly disabledRoleLimitsStrategyService = inject(DisabledRoleLimitsStrategyService);
  private readonly organizationMemberRoleLimitsStrategyService = inject(OrganizationMemberRoleLimitsStrategyService);
  protected readonly localeService = inject(LocaleService);

  get fullName(): string | undefined {
    return [
      this.user.salutation ? this.translocoService.translate(`user.salutation.${this.user?.salutation}`) : null,
      this.user?.firstname,
      this.user?.lastname,
    ]
      .filter(Boolean)
      .join(' ');
  }

  ngOnInit(): void {
    if (this.demoContent) {
      this.setDemoContent(this.demoContent);
      this.isLoading = false;
      return;
    }

    const limitService = AppConfigService.isFeatureEnabled(AppFeature.ROLE_LIMITS)
      ? this.organizationMemberRoleLimitsStrategyService
      : this.disabledRoleLimitsStrategyService;

    this.cartridgeRoleAssignmentDataService.init(limitService);
    this.userId = this.route.snapshot.paramMap.get('userId') as string;

    this.getOrganizationId()
      .pipe(
        tap(orgId => (this.organizationId = orgId)),
        switchMap(() => this.getRequesterRoles()),
        mergeMap(() => this.getRequesterOrganizationId()),
        mergeMap(() => this.getAssignableOrganizationRole()),
        mergeMap(() => this.loadData()),
        tap(() => this.initAuthorization()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.translocoService.langChanges$
      .pipe(
        skip(1),
        switchMap(() => this.getAssignableOrganizationRole()),
        mergeMap(() => this.loadData()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private setDemoContent(config: MemberDetailsDemoConfig): void {
    this.user = config.user;
    this.usageRights = config.usageRights;
    this.reducedCartridgeRoleAssignments = config.cartridgeRoleAssignments;
    this.userGroups = config.userGroups;
    this.groupRoleAssignments = config.groupRoleAssignments;
  }

  private getRequesterRoles(): Observable<string[]> {
    return this.authenticatedUserService.getUserRoles().pipe(
      map(roles => roles.map(({ name }) => name)),
      tap(roles => {
        this.userRoles = roles;
        this.currentUserIsPlatformAdmin = roles?.includes(StaticRole.PLATFORM_ADMIN);
        this.currentUserIsOrgAdmin = roles?.includes(StaticRole.ORGANIZATION_ADMIN);
        this.currentUserShowsSelf = this.authenticatedUserService.userId === this.userId;
      })
    );
  }

  private getRequesterOrganizationId(): Observable<string> {
    return this.authenticatedUserService.getOrganizationId().pipe(
      tap(orgId => {
        this.currentUserOrganizationId = orgId;
      })
    );
  }

  private determineNoMailChangeReason(): void {
    if (this.currentUserIsPlatformAdmin) {
      if (!this.userHasActiveMembership(this.user)) {
        this.noEmailChangeReason.set('notActive');
      } else if (this.user.ssoEnabled) {
        this.noEmailChangeReason.set('sso');
      } else if (this.user.invitedOrganizationMemberships) {
        this.noEmailChangeReason.set('invited');
      }
    }
  }

  loadData(): Observable<void> {
    if (!this.userRoles.length) {
      return of(undefined);
    }
    this.isLoading = true;

    return this.userService.getUserForOrganization(this.userId, this.organizationId).pipe(
      catchError(() => {
        this.isLoading = false;
        this.snackbarService.showErrorSnackbarWithTransloco('user.details.loadingError');
        return EMPTY;
      }),
      tap(user => {
        this.isLoading = false;
        this.patchUserData(user);
        this.selectAndPatchOrganizationRole();
        this.membershipOrigin$ = this.getMembership(this.userId, this.organizationId);
      }),
      switchMap(() => this.authorizerService.getAllGroupsByUserId(this.userId, this.organizationId)),
      tap(groups => (this.userGroups = groups.sort((a, b) => a.displayName.localeCompare(b.displayName)))),
      switchMap(() => this.loadUsageRightsAndRoles()),
      tap(useRoles => {
        this.mapGroupRoles(useRoles);
        this.determineNoMailChangeReason();
        if (this.currentUserIsPlatformAdmin && !this.noEmailChangeReason()) {
          this.userOrganizationForm.get('email')?.enable();
        }
        this.saveFormInitialValues();
        this.disableButtonChanges();
      }),
      map(() => void 0)
    );
  }

  getMembership(userId: string, organizationId: string): Observable<MembershipOriginInformation> {
    return this.userService.getMembershipOrigin(userId, organizationId);
  }

  getCartridgeNameByVariantId(cartridgeVariantId: string): string {
    const usageRight = this.usageRights?.find(i => i.cartridgeVariantId === cartridgeVariantId);
    if (usageRight) {
      return usageRight.cartridgeName;
    }
    return 'ID ' + cartridgeVariantId;
  }

  changeEditMode(editing: boolean): void {
    this.editingMode = editing;
    if (!this.user.address) {
      this.user.address = this.emptyAddress;
    }
    this.userOrganizationForm.reset(this.user);
    this.saveFormInitialValues();
  }

  finishEdit(editAuditReason?: string): void {
    combineLatest([
      this.updateOrganizationRolesIfNecessary().pipe(
        catchError(() => {
          this.snackbarService.showErrorSnackbarWithTransloco('error.common');
          this.isLoading = false;
          return EMPTY;
        })
      ),
      this.updateUserDataIfNecessary(editAuditReason).pipe(
        catchError(({ error }: HttpErrorResponse) => {
          this.snackbarService.showErrorSnackbarWithTransloco(
            OrganizationMemberDetailsComponent.handledErrorCodes.includes(error.errorCode)
              ? `error.${error.errorCode.replace('-', '.')}`
              : 'error.common'
          );
          this.isLoading = false;
          return EMPTY;
        })
      ),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.isLoading = false;
        this.changeEditMode(false);
        this.snackbarService.showSuccessSnackbarWithTransloco('user.details.updateSuccess');
        this.saveFormInitialValues();
      });
  }

  patchUserData(userData: OrganizationUserDTO): void {
    this.user = userData;
    this.userOrganizationForm.patchValue(this.user);
  }

  openRoleAssignmentDialog(): void {
    this.cartridgeRoleAssignmentDataService.resetSelectedRoles();
    if (this.usageRights.length === 0) {
      this.snackbarService.showErrorSnackbarWithTransloco(
        'user.details.role-assignment.noServiceBookedFromOrganization'
      );
      return;
    }
    this.isRoleAssignmentDialogOpen = true;
  }

  resetDeletionCallback(open: boolean): void {
    if (!open) {
      this.deleteConfirmationDialogCallback = undefined;
    }
  }

  trackByFn(index: number): number {
    return index;
  }

  getRoleLabel(userRole: UserRole): string {
    let rolePrefix = undefined;
    if (userRole.groupId) {
      rolePrefix = `(${this.translocoService.translate('common.group')})`;
    }
    if (userRole.suborganizationId) {
      rolePrefix = `(${this.translocoService.translate('user.details.role-assignment.location-affiliation')})`;
    }
    return [rolePrefix, userRole.localizedDisplayName].filter(Boolean).join(' ');
  }

  getGroupRoleLabel(userRole: UserRole): string {
    let rolePrefix = undefined;
    if (userRole.suborganizationId) {
      rolePrefix = `(${this.translocoService.translate('user.details.role-assignment.location-affiliation')})`;
    }
    return [rolePrefix, userRole.localizedDisplayName].filter(Boolean).join(' ');
  }

  private loadUsageRightsAndRoles(): Observable<UserRole[]> {
    return this.usageRightService
      .getUsageRightsForOrganization(
        this.currentUserIsPlatformAdmin ? this.organizationId : 'current',
        0,
        0,
        'active=true'
      )
      .pipe(
        take(1),
        tap(allUsageRights => (this.usageRights = allUsageRights.result)),
        switchMap(_ => this.loadCartridgeRoleAssignments())
      );
  }

  openResendInvitationMailDialog(): void {
    this.isResendInvitationMailDialogOpen = true;
  }

  openResendEmailVerificationEmail(): void {
    this.isResendVerificationEmailOpen = true;
  }

  resendInvitationMail(): void {
    this.isResendInvitationMailDialogOpen = false;

    this.userService
      .resendInvitationMail(this.userId, this.organizationId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => {
          this.snackbarService.showErrorSnackbarWithTransloco('user.details.resendInvitationMail.dialog.error');
          return EMPTY;
        })
      )
      .subscribe(() =>
        this.snackbarService.showSuccessSnackbarWithTransloco('user.details.resendInvitationMail.dialog.success')
      );
  }

  resendVerificationMail(): void {
    this.isResendVerificationEmailOpen = false;

    this.userService
      .resendVerificationEmail(this.userId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => {
          this.snackbarService.showErrorSnackbarWithTransloco();
          return EMPTY;
        })
      )
      .subscribe(() =>
        this.snackbarService.showSuccessSnackbarWithTransloco('user.details.resendVerificationMail.dialog.success')
      );
  }

  removeOrganizationAdminRole(
    newOwners?: {
      userId: string;
      cartridgeVariantId: string;
    }[]
  ): void {
    this.isLoading = true;
    const organizationAdminRole = this.assignableOrganizationRoles.find(
      a => a.internalName === StaticRole.ORGANIZATION_ADMIN
    );
    if (organizationAdminRole) {
      this.authorizerService
        .removeUsersWithAssignNewServiceResponsibleAdmin(
          organizationAdminRole,
          this.organizationId,
          [this.userId],
          newOwners
        )
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          catchError(_ => {
            this.isLoading = false;
            this.snackbarService.showErrorSnackbarWithTransloco('error.common');
            return EMPTY;
          })
        )
        .subscribe(() => {
          this.isLoading = false;
          this.selectAndPatchOrganizationRole(StaticRole.ORGANIZATION_MEMBER);
        });
    }
  }

  triggerReload(): void {
    this.loadData().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  userStatusToDeleted(hasSuccess: boolean): void {
    if (!hasSuccess) {
      return;
    }

    if (this.location.path().includes('platform-requests-audit')) {
      const platformRequestId = this.route.snapshot.paramMap.get('id') as string;

      this.router.navigate(['backoffice', 'platform-requests-audit', platformRequestId]);
      return;
    }

    if (this.currentUserIsPlatformAdmin) {
      this.user.userOrganizationStatus = UserOrganizationStatus.DELETED;
      return;
    }

    if (this.currentUserIsOrgAdmin) {
      this.router.navigate(['organization', 'organization-overview', 'members']);
      return;
    }
  }

  async checkFormAndSave(): Promise<void> {
    this.userOrganizationForm.markAllAsTouched();
    if (!this.userRoles.length || this.userOrganizationForm.invalid) {
      return;
    }

    if (this.userOrganizationForm.get('email')?.dirty) {
      this.confirmMailChangeDialogOpen.set(true);
      if (!(await firstValueFrom(this.confirmedMailChange$))) {
        return;
      }
    }

    if (this.currentUserIsPlatformAdmin) {
      this.isAuditReasonDialogOpen = true;
    } else {
      this.finishEdit();
    }
  }

  saveUserWithReason(value: string): void {
    this.isAuditReasonDialogOpen = false;
    this.finishEdit(value);
  }

  closeAssignAdminForServiceBookingResponsibilityDialog(): void {
    this.isAssignAdminForServiceBookingResponsibilityOpen = false;
  }

  closeAuditReasonDialog(): void {
    this.isAuditReasonDialogOpen = false;
  }

  closeEmployeeRemoveDialog(): void {
    this.removeEmployeeDialogOpen = false;
  }

  keepOrder(): number {
    return 1;
  }

  onDeleteEmployee(): void {
    this.removeEmployeeDialogOpen = true;
  }

  triggerConfirmMailChange(confirmed: boolean): void {
    this.confirmedMailChange$.next(confirmed);
  }

  closeConfirmMailChangeDialog(): void {
    this.confirmMailChangeDialogOpen.set(false);
  }

  private updateUserDataIfNecessary(editAuditReason?: string): Observable<OrganizationUserDTO> {
    if (!this.userOrganizationForm.dirty) {
      return of(this.user);
    }
    const updatedData = {
      ...this.userOrganizationForm.value,
      phone: removeWhitespace(this.userOrganizationForm.controls.phone.value),
    };
    setEmptyStringPropertiesToNull(updatedData);
    return this.userService
      .updateUserForOrganization(
        this.userId,
        this.organizationId,
        {
          ...updatedData,
          version: this.user.version,
        } as UserUpdateDTO,
        editAuditReason
      )
      .pipe(
        map(user => {
          return {
            ...this.user,
            ...user,
            salutation: SalutationType[user.salutation as SalutationType],
          } as OrganizationUserDTO;
        }),
        tap(userData => {
          this.patchUserData(userData);
        })
      );
  }

  private getOrganizationId(): Observable<string> {
    // The org id is only present for the navigation of a platform admin in the backoffice.
    // For roles like organization admin the id is not present due to the user has an active org session,
    // specified by the current orgId claim in the users JWT from our SSO.
    const orgIdFromRoute = this.route.snapshot.paramMap.get('organizationId') as string;
    return orgIdFromRoute ? of(orgIdFromRoute) : this.authenticatedUserService.getOrganizationId();
  }

  private initAuthorization(): void {
    this.currentUserIsAllowedToManageGroupAssignments =
      this.currentUserOrganizationId === this.organizationId &&
      this.userRoles.some(r =>
        ([StaticRole.ORGANIZATION_ADMIN, StaticRole.ORGANIZATION_MANAGER] as string[]).includes(r)
      );
  }

  private loadCartridgeRoleAssignments(): Observable<UserRole[]> {
    return this.authorizerService.getCartridgeRolesForUser(this.userId, this.organizationId).pipe(
      take(1),
      catchError(() => {
        this.handleErrorLoadCartridgeRoleAssignments();
        return EMPTY;
      }),
      tap(data => this.handleSuccessLoadCartridgeRoleAssignments(data))
    );
  }

  private handleSuccessLoadCartridgeRoleAssignments(data: UserRole[]): void {
    this.cartridgeRoleAssignmentDataService.setPreviousCartridgeRoleAssignmentsFromUserRoles(data);

    const temp = data.reduce((map, userRole) => {
      const key = userRole.leistungId as string;
      if (!map.get(key)?.find(ur => ur.id === userRole.id) && !userRole.groupId) {
        map.get(key)?.push(userRole) ?? map.set(key, [userRole]);
      }
      return map;
    }, new Map<string, UserRole[]>());
    this.reducedCartridgeRoleAssignments = new Map<string, UserRole[]>(
      [...temp].sort((a, b) =>
        (a[1][0].localizedCartridgeName || '').localeCompare(b[1][0].localizedCartridgeName || '')
      )
    );
  }

  private handleErrorLoadCartridgeRoleAssignments(): void {
    this.calloutColorScheme = 'error';
    this.calloutMsgKey = 'user.errorLoadingRoles';
    this.showLoadRolesError = true;
  }

  private selectAndPatchOrganizationRole(newRole?: StaticRole): void {
    if (newRole === StaticRole.ORGANIZATION_ADMIN) {
      pushIfNotExist(this.user.platformRoles, newRole);
      pushIfNotExist(this.user.roles, newRole);
    }
    if (newRole === StaticRole.ORGANIZATION_MEMBER) {
      this.user.platformRoles = this.user.platformRoles?.filter(role => role !== StaticRole.ORGANIZATION_ADMIN);
      this.user.roles = this.user.roles.filter(role => role !== StaticRole.ORGANIZATION_ADMIN);
    }
    this.user.roles.sort((a, b) => compareRole(a, b));
    const role = this.user.roles[0];
    if (role === StaticRole.ORGANIZATION_MEMBER) {
      pushIfNotExist(this.user.platformRoles, StaticRole.ORGANIZATION_MEMBER);
    }
    this.organizationRoleForm.controls.organizationRole.setValue(role);
    if (this.assignableOrganizationRoles.length === 0) {
      this.assignableOrganizationRoles.push({ internalName: role } as Role);
    }
    if (this.assignableOrganizationRoles.length <= 1) {
      this.organizationRoleForm.controls.organizationRole.disable();
    } else {
      this.organizationRoleForm.controls.organizationRole.enable();
    }
  }

  private updateOrganizationRolesIfNecessary(): Observable<void> {
    const newRole = this.organizationRoleForm.controls.organizationRole.value;
    const organizationAdminRole = this.assignableOrganizationRoles.find(
      a => a.internalName === StaticRole.ORGANIZATION_ADMIN
    );
    if (!newRole || !this.organizationRoleForm.dirty || !organizationAdminRole) {
      return of(void 0);
    }

    if (newRole === StaticRole.ORGANIZATION_ADMIN && !this.user.roles.includes(StaticRole.ORGANIZATION_ADMIN)) {
      return this.authorizerService
        .assignRoleToUser({
          userId: this.userId,
          organizationId: this.organizationId,
          roleId: organizationAdminRole.id,
        })
        .pipe(tap(() => this.selectAndPatchOrganizationRole(newRole)));
    }

    if (newRole === StaticRole.ORGANIZATION_MEMBER && this.user.roles.includes(StaticRole.ORGANIZATION_ADMIN)) {
      return this.checkAlternativeCartridgeVariantOwners(organizationAdminRole);
    }
    return of(void 0);
  }

  private checkAlternativeCartridgeVariantOwners(organizationAdminRole: Role): Observable<void> {
    return this.userService.checkAlternativeCartridgeVariantOwners(this.organizationId, [this.userId]).pipe(
      switchMap(alternativeCartridgeVariantOwners => {
        if (alternativeCartridgeVariantOwners?.owners?.length) {
          this.owners = alternativeCartridgeVariantOwners.owners;
          this.alternativeOwners = alternativeCartridgeVariantOwners.alternativeOwners;
          this.isAssignAdminForServiceBookingResponsibilityOpen = true;
          this.isLoading = false;
          return of(void 0);
        } else {
          return this.authorizerService
            .removeUsersWithAssignNewServiceResponsibleAdmin(organizationAdminRole, this.organizationId, [this.userId])
            .pipe(tap(() => this.selectAndPatchOrganizationRole(StaticRole.ORGANIZATION_MEMBER)));
        }
      })
    );
  }

  private mapGroupRoles(userRoles: UserRole[]): void {
    this.groupRoleAssignments = {};

    userRoles
      .filter(({ groupId, leistungId }) => groupId !== undefined && leistungId !== undefined)
      .forEach(role => {
        const groupId = role.groupId as string;
        const cartridgeId = role.leistungId as string;
        const localizedCartridgeName = role.localizedCartridgeName as string;
        if (!this.groupRoleAssignments[groupId]) {
          this.groupRoleAssignments[groupId] = [];
        }
        const existingAssignmentGroup = this.groupRoleAssignments[groupId].find(
          item => item.cartridgeId === cartridgeId
        );
        if (existingAssignmentGroup) {
          const existingGroupRoleAssignment = existingAssignmentGroup.roles.find(r => r.id === role.id);
          if (!existingGroupRoleAssignment) {
            existingAssignmentGroup.roles.push(role);
          }
        } else {
          this.groupRoleAssignments[groupId].push({
            cartridgeId,
            localizedCartridgeName,
            roles: [role],
          });
        }
      });

    this.sortGroupRoleAssignments();
  }

  private sortGroupRoleAssignments(): void {
    for (const groupId of Object.keys(this.groupRoleAssignments)) {
      this.groupRoleAssignments[groupId].sort((a, b) => {
        return a.localizedCartridgeName.localeCompare(b.localizedCartridgeName);
      });

      this.groupRoleAssignments[groupId].forEach(groupAssignment => {
        groupAssignment.roles = [...groupAssignment.roles].sort((a: UserRole, b: UserRole) =>
          a.localizedDisplayName.localeCompare(b.localizedDisplayName)
        );
      });
    }
  }

  private getAssignableOrganizationRole(): Observable<Role[]> {
    return this.authorizerService
      .getAssignableOrganizationRoles(this.organizationId)
      .pipe(tap(assignableRoles => (this.assignableOrganizationRoles = assignableRoles)));
  }

  private disableButtonChanges(): void {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.submitDisabled = isFormUnChanged(this.form.getRawValue(), this.initialFormValues);
    });
  }

  private userHasActiveMembership({
    userOrganizationStatus,
    activeOrganizationMemberships,
  }: OrganizationUserDTO): boolean {
    if (this.currentUserIsPlatformAdmin) {
      return activeOrganizationMemberships ?? false;
    }

    return userOrganizationStatus === UserOrganizationStatus.ACTIVE;
  }

  private saveFormInitialValues(): void {
    this.initialFormValues = this.form.getRawValue();
  }
}

const pushIfNotExist = <T>(array?: T[], item?: T): void => {
  if (array && item && !array.includes(item)) {
    array.push(item);
  }
};
